import { useUpdateOrganizationUserMutation } from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { BirthdayPicker } from '../../components/ui/birthday-picker';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

export interface ContactData {
	id: string;
	firstName: string;
	lastName: string;
	email?: string | null;
	jobPosition?: string | null;
	phone?: string | null;
	birthday?: string | null;
	notes?: string | null;
	organizationName?: string | null;
}

export function EditContactModal({
	open,
	onOpenChange,
	contact,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	contact: ContactData | null;
	onSuccess?: () => void;
}) {
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [jobPosition, setJobPosition] = useState('');
	const [phone, setPhone] = useState('');
	const [birthday, setBirthday] = useState<string | null>(null);
	const [notes, setNotes] = useState('');

	const [updateOrganizationUser, { loading }] = useUpdateOrganizationUserMutation();

	useEffect(() => {
		if (open && contact) {
			setFirstName(contact.firstName);
			setLastName(contact.lastName);
			setJobPosition(contact.jobPosition || '');
			setPhone(contact.phone || '');
			setBirthday(contact.birthday || null);
			setNotes(contact.notes || '');
		}
	}, [open, contact]);

	const handleSubmit = async () => {
		if (!contact || !firstName.trim() || !lastName.trim()) return;

		try {
			const { data } = await updateOrganizationUser({
				variables: {
					input: {
						id: contact.id,
						firstName: firstName.trim(),
						lastName: lastName.trim(),
						// null (not undefined) so emptying a field actually clears it
						jobPosition: jobPosition.trim() || null,
						phone: phone.trim() || null,
						birthday: birthday,
						notes: notes.trim() || null,
					},
				},
			});

			if (data?.updateOrganizationUser?.success) {
				toast.success('Contact updated');
				onOpenChange(false);
				onSuccess?.();
			} else {
				toast.error(data?.updateOrganizationUser?.message || 'Failed to update contact');
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update contact');
		}
	};

	const canSubmit = firstName.trim() && lastName.trim();

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Edit contact</DialogTitle>
					<DialogDescription>
						{contact?.organizationName
							? `Update this contact at ${contact.organizationName}.`
							: 'Update this contact’s details.'}
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							{contact?.email && (
								<div className="flex flex-col gap-1.5">
									<Label>Email</Label>
									<Input value={contact.email} disabled readOnly />
								</div>
							)}

							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="edit-contact-first-name">
										First name <span className="text-destructive">*</span>
									</Label>
									<Input
										id="edit-contact-first-name"
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
										placeholder="First name"
									/>
								</div>
								<div className="flex flex-col gap-1.5">
									<Label htmlFor="edit-contact-last-name">
										Last name <span className="text-destructive">*</span>
									</Label>
									<Input
										id="edit-contact-last-name"
										value={lastName}
										onChange={(e) => setLastName(e.target.value)}
										placeholder="Last name"
									/>
								</div>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="edit-contact-job-position">Job position</Label>
								<Input
									id="edit-contact-job-position"
									value={jobPosition}
									onChange={(e) => setJobPosition(e.target.value)}
									placeholder="e.g. Head of Procurement"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="edit-contact-phone">Phone</Label>
								<Input
									id="edit-contact-phone"
									type="tel"
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
									placeholder="+45 12 34 56 78"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>Birthday</Label>
								<BirthdayPicker value={birthday} onChange={setBirthday} />
							</div>

							<div className="flex flex-col gap-1.5">
								<Label htmlFor="edit-contact-notes">Internal notes</Label>
								<Textarea
									id="edit-contact-notes"
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									placeholder="Only visible to admins"
									rows={3}
								/>
							</div>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => onOpenChange(false)}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!canSubmit || loading}>
							{loading ? 'Saving…' : 'Save changes'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
